import {
  type ServiceTicketCreateSchemaType,
  type TargetChip,
  serviceTicketCreateSchema,
} from '@/forms/serviceTicket/create/schema';
import { TargetSection } from '@/forms/serviceTicket/create/sections/TargetSection';
import { TicketDetailsSection } from '@/forms/serviceTicket/create/sections/TicketDetailsSection';
import { useServiceTicketCreateSubmit } from '@/forms/serviceTicket/create/useServiceTicketCreateSubmit';
import { BulkCreateResults } from '@/forms/shared/BulkCreateResults';
import { MAX_BULK_TARGETS } from '@/forms/shared/targetChip';
import { casesApi } from '@/store/api/services/operations/cases';
import type { BulkCreateServiceTicketResult, TaskType } from '@/types/serviceTicket';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';
import { zodResolver } from '@hookform/resolvers/zod';
import { Assignment as AssignmentIcon } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Button, CardContent, DialogActions, DialogContent } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

interface CreateServiceTicketProps {
  // Single-target entry point: a specific case (Cases list row action, Case detail page).
  caseId?: string;
  // Bulk entry point: multiple cases selected from the Cases list - each case's location
  // becomes a pre-resolved, already-valid target chip (tickets are location-scoped).
  bulkLocationIds?: number[];
}

const CreateServiceTicket = ({ hide, props }: ModalChildrenProps) => {
  const { caseId, bulkLocationIds: allBulkLocationIds = [] }: CreateServiceTicketProps =
    props || {};
  // Boro's bulk create endpoint rejects more than MAX_BULK_TARGETS combined ids - trim the
  // Cases-list selection to that limit rather than letting it fail at submit time (same as the
  // full create page's router-state entry point).
  const bulkLocationIds = allBulkLocationIds.slice(0, MAX_BULK_TARGETS);
  const hasBulkSelection = bulkLocationIds.length > 0;
  const [bulkResults, setBulkResults] = useState<BulkCreateServiceTicketResult[] | null>(null);

  // Tasks to assign to the ticket after it is created (created via a separate endpoint in
  // single mode; sent inline as task_type_ids in bulk mode).
  const [selectedTaskTypes, setSelectedTaskTypes] = useState<TaskType[]>([]);

  useEffect(() => {
    if (allBulkLocationIds.length > MAX_BULK_TARGETS) {
      toast.error(
        `Only the first ${MAX_BULK_TARGETS} selected cases were kept - bulk service ticket creation allows at most ${MAX_BULK_TARGETS} at once.`,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bulkTargets: TargetChip[] = bulkLocationIds.map((id) => ({
    type: 'location' as const,
    value: id.toString(),
    status: 'valid' as const,
  }));

  const {
    handleSubmit,
    register,
    control,
    watch,
    setValue,
    formState: { isSubmitting, errors },
  } = useForm<ServiceTicketCreateSchemaType>({
    resolver: zodResolver(serviceTicketCreateSchema),
    defaultValues: {
      targetMode: hasBulkSelection ? 'id' : 'single',
      case_id: caseId || '',
      targets: bulkTargets,
    },
  });

  const targetMode = watch('targetMode');
  const targetCount = watch('targets')?.length ?? 0;
  const watchedCaseId = watch('case_id');

  // Fetch case data to get location_id, whether preset via caseId or picked in the single tab
  const { data: selectedCaseData } = casesApi.useGetCaseQuery(watchedCaseId || '', {
    skip: !watchedCaseId,
  });

  // location_id only matters in single-target mode; gate this so picking a shared case in
  // bulk mode never overwrites the id-mode location_id.
  useEffect(() => {
    if (targetMode !== 'single') return;

    if (watchedCaseId && selectedCaseData?.data?.location_id) {
      setValue('location_id', selectedCaseData.data.location_id.toString(), {
        shouldValidate: true,
        shouldDirty: true,
      });
    } else if (!watchedCaseId) {
      setValue('location_id', '', {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [targetMode, watchedCaseId, selectedCaseData?.data?.location_id, setValue]);

  const { onSubmit, isSubmitting: isCreating } = useServiceTicketCreateSubmit({
    selectedCase: selectedCaseData?.data,
    selectedTaskTypes,
    onSingleCreated: hide,
    onBulkCreated: setBulkResults,
  });

  if (bulkResults) {
    return (
      <DialogContent>
        <CardContent>
          <BulkCreateResults
            results={bulkResults.map((result) => ({
              ...result,
              entity_id: result.service_ticket_id,
            }))}
            entityLabel="service ticket"
            columnLabel="Ticket"
            clickableName="Service Ticket"
            createMoreLabel="Create More Tickets"
            backLabel="Done"
            detailPath={(id) => `/service-ticket/${id}`}
            onDone={hide}
            onCreateMore={() => {
              setBulkResults(null);
              setSelectedTaskTypes([]);
              setValue('targets', [], { shouldValidate: false });
            }}
          />
        </CardContent>
      </DialogContent>
    );
  }

  return (
    <form
      id="service-ticket-form"
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit(
          (data) => onSubmit(data),
          () => toast.error('Please fix the form errors before submitting'),
        )(e);
      }}
    >
      <DialogContent>
        <CardContent>
          <TargetSection control={control} errors={errors} setValue={setValue} watch={watch} />

          <TicketDetailsSection
            register={register}
            control={control}
            errors={errors}
            selectedTaskTypes={selectedTaskTypes}
            onSelectedTaskTypesChange={setSelectedTaskTypes}
          />
        </CardContent>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={hide} disabled={isSubmitting || isCreating}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          type="submit"
          loading={isSubmitting || isCreating}
          startIcon={<AssignmentIcon />}
          data-test="service-ticket-submit-button"
        >
          {targetMode === 'id' && targetCount > 1
            ? 'Create Service Tickets'
            : 'Create Service Ticket'}
        </LoadingButton>
      </DialogActions>
    </form>
  );
};

export default CreateServiceTicket;
